if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.playThumbnailLeftStagger();
      }

      isThumbnailLeftLayout() {
        return this.dataset.desktopLayout === 'thumbnail_left';
      }

      getThumbnailList() {
        return this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails"]');
      }

      isVerticalThumbVisible(thumbnail, list, offset = 10) {
        const listRect = list.getBoundingClientRect();
        const thumbRect = thumbnail.getBoundingClientRect();
        return thumbRect.top >= listRect.top - offset && thumbRect.bottom <= listRect.bottom + offset;
      }

      playThumbnailLeftStagger() {
        if (!this.isThumbnailLeftLayout() || !this.mql.matches || !this.elements.thumbnails) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const slider = this.elements.thumbnails;
        if (slider.dataset.staggerPlayed === 'true') return;
        slider.dataset.staggerPlayed = 'true';
        slider.classList.add('thumbnail-slider--stagger');

        const items = slider.querySelectorAll('.thumbnail-list__item');
        if (!items.length) {
          slider.classList.remove('thumbnail-slider--stagger');
          return;
        }

        let remaining = items.length;
        const done = () => {
          remaining -= 1;
          if (remaining <= 0) slider.classList.remove('thumbnail-slider--stagger');
        };

        items.forEach((item) => {
          item.addEventListener('animationend', done, { once: true });
        });

        // Fallback if animationend does not fire
        window.setTimeout(() => {
          slider.classList.remove('thumbnail-slider--stagger');
        }, 1200);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        const list = this.getThumbnailList();
        const useVertical =
          this.isThumbnailLeftLayout() && this.mql.matches && list && getComputedStyle(list).overflowY !== 'visible';

        if (useVertical) {
          if (this.isVerticalThumbVisible(thumbnail, list)) return;
          const listRect = list.getBoundingClientRect();
          const thumbRect = thumbnail.getBoundingClientRect();
          list.scrollTo({
            top: list.scrollTop + (thumbRect.top - listRect.top) - list.clientHeight / 2 + thumbRect.height / 2,
            behavior: 'smooth',
          });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
